import React, { useState } from 'react';
import { Users, Eye, Check, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useWines } from '@/contexts/WinesContext';
import { useAuth } from '@/contexts/AuthContext';
import type { ShareAccessType, UserConnection } from '@/types';

interface SelectConnectedUsersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  wineIds: string[]; // ID dei vini da condividere
  wineNames?: string[]; // Nomi vini (per display)
}

export const SelectConnectedUsersDialog: React.FC<SelectConnectedUsersDialogProps> = ({
  isOpen,
  onClose,
  wineIds,
  wineNames,
}) => {
  const { user } = useAuth();
  const { connectedUsers, shareWinesWithUsers, wines } = useWines();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [accessType, setAccessType] = useState<ShareAccessType>('read-only');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Filtra utenti che NON hanno già accesso a TUTTI i vini selezionati
  const availableUsers = connectedUsers.filter(connection => {
    const otherUserId = connection.userA === user?.id ? connection.userB : connection.userA;

    // Verifica se l'utente ha già accesso a tutti i vini
    return !wineIds.every(wineId => {
      const wine = wines.find(w => w.id === wineId);
      return wine?.sharedWithUserIds?.includes(otherUserId);
    });
  });

  const handleClose = () => {
    setSelectedUserIds([]);
    setAccessType('read-only');
    setLoading(false);
    setSuccess(false);
    onClose();
  };

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleShare = async () => {
    if (selectedUserIds.length === 0 || wineIds.length === 0) return;

    // Filtra vini bevuti - non possono essere condivisi
    const cellarWineIds = wineIds.filter(wineId => {
      const wine = wines.find(w => w.id === wineId);
      return wine?.status === 'cellar';
    });

    if (cellarWineIds.length === 0) {
      alert('I vini bevuti non possono essere condivisi');
      return;
    }

    setLoading(true);

    try {
      await shareWinesWithUsers(cellarWineIds, selectedUserIds, accessType);
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error sharing wines:', error);
      alert('Errore durante la condivisione');
    } finally {
      setLoading(false);
    }
  };

  // Helper per ottenere info dell'altro utente nella connessione
  const getOtherUserInfo = (connection: UserConnection) => {
    if (!user) return { id: '', name: '', email: '' };
    const isUserA = connection.userA === user.id;
    return {
      id: isUserA ? connection.userB : connection.userA,
      name: isUserA ? connection.userBName : connection.userAName,
      email: isUserA ? connection.userBEmail : connection.userAEmail,
    };
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Condividi {wineIds.length === 1 ? 'Vino' : 'Vini'}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Seleziona gli utenti con cui condividere {wineIds.length === 1 ? 'questo vino' : `questi ${wineIds.length} vini`}
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {success ? (
              /* Success State */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {wineIds.length === 1 ? 'Vino Condiviso!' : 'Vini Condivisi!'}
                </h3>
                <p className="text-sm text-gray-600">
                  {wineIds.length === 1 ? 'Il vino è' : `I ${wineIds.length} vini sono`} ora visibil{wineIds.length === 1 ? 'e' : 'i'} agli utenti selezionati
                </p>
              </div>
            ) : availableUsers.length === 0 ? (
              /* No Available Users State */
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {connectedUsers.length === 0
                    ? 'Nessun Utente Connesso'
                    : 'Nessun Utente Disponibile'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {connectedUsers.length === 0
                    ? 'Devi prima creare una connessione con un utente dal tuo profilo'
                    : wineIds.length === 1
                    ? 'Questo vino è già condiviso con tutti i tuoi utenti connessi'
                    : 'Questi vini sono già condivisi con tutti i tuoi utenti connessi'}
                </p>
                {connectedUsers.length === 0 && (
                  <Button onClick={handleClose} variant="outline">
                    Chiudi
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Wine Names Display */}
                {wineNames && wineNames.length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {wineIds.length === 1 ? 'Vino da condividere:' : 'Vini da condividere:'}
                    </p>
                    <div className="space-y-1">
                      {wineNames.map((name, index) => (
                        <p key={index} className="text-sm text-gray-600">
                          • {name}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Select Users */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Seleziona Utenti
                  </h3>
                  <div className="space-y-2">
                    {availableUsers.map((connection) => {
                      const otherUser = getOtherUserInfo(connection);
                      const isSelected = selectedUserIds.includes(otherUser.id);

                      return (
                        <Card
                          key={connection.id}
                          padding="sm"
                          hover
                          onClick={() => toggleUser(otherUser.id)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            {/* Checkbox */}
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected
                                  ? 'bg-wine-purple border-wine-purple'
                                  : 'border-gray-300'
                              }`}
                            >
                              {isSelected && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>

                            {/* User Avatar */}
                            <div className="w-10 h-10 rounded-full bg-wine-purple/10 flex items-center justify-center flex-shrink-0">
                              <Users className="w-5 h-5 text-wine-purple" />
                            </div>

                            {/* User Info */}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {otherUser.name || 'Utente'}
                              </p>
                              <p className="text-sm text-gray-600 truncate">
                                {otherUser.email}
                              </p>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  {selectedUserIds.length === 0 && (
                    <p className="text-sm text-gray-500 mt-3 text-center">
                      Seleziona almeno un utente per continuare
                    </p>
                  )}
                </div>

                {/* Access Type Selection */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Tipo di Accesso
                  </h3>
                  <div className="space-y-3">
                    {/* Read-only */}
                    <Card
                      padding="md"
                      hover
                      onClick={() => setAccessType('read-only')}
                      className={`cursor-pointer border-2 transition-colors ${
                        accessType === 'read-only'
                          ? 'border-wine-purple bg-wine-purple/5'
                          : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            accessType === 'read-only'
                              ? 'border-wine-purple'
                              : 'border-gray-300'
                          }`}
                        >
                          {accessType === 'read-only' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-wine-purple" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Eye className="w-5 h-5 text-blue-600" />
                            <h4 className="font-semibold text-gray-900">Solo Lettura</h4>
                          </div>
                          <p className="text-sm text-gray-600">
                            Gli utenti potranno vedere {wineIds.length === 1 ? 'il vino' : 'i vini'} ma non potranno modificarli, eliminarli o segnarli come bevuti
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Full access */}
                    <Card
                      padding="md"
                      hover
                      onClick={() => setAccessType('full')}
                      className={`cursor-pointer border-2 transition-colors ${
                        accessType === 'full'
                          ? 'border-wine-purple bg-wine-purple/5'
                          : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            accessType === 'full'
                              ? 'border-wine-purple'
                              : 'border-gray-300'
                          }`}
                        >
                          {accessType === 'full' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-wine-purple" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-5 h-5 text-green-600" />
                            <h4 className="font-semibold text-gray-900">Accesso Completo</h4>
                          </div>
                          <p className="text-sm text-gray-600">
                            Gli utenti avranno gli stessi privilegi: potranno modificare, eliminare e segnare {wineIds.length === 1 ? 'il vino' : 'i vini'} come bevuti
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-900">
                      <strong>Nota:</strong> I vini bevuti sono sempre personali e non verranno mai condivisi.
                      Se segni un vino come bevuto, rimarrà nella tua cantina personale.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!success && connectedUsers.length > 0 && (
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <Button variant="outline" onClick={handleClose} disabled={loading}>
                Annulla
              </Button>
              <Button
                onClick={handleShare}
                loading={loading}
                disabled={selectedUserIds.length === 0 || loading}
                icon={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              >
                Condividi
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
